/**
 * Centro de auditoria: control.audit_logs (vover-app) + control.billing_notification_audit (webhooks billing).
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tabs,
  TextField,
  Typography,
  Stack,
  Card,
  CardContent
} from '@mui/material';
import { Download, Refresh, ScheduleSend } from '@mui/icons-material';
import { apiService } from '@/services/api';
import { ClientCell } from '@/components/ClientCell';
import { CopyableId } from '@/components/CopyableId';
import { ProductCell } from '@/components/ProductCell';
import { useFilters } from '@/contexts/FilterContext';
import { useRefresh } from '@/contexts/RefreshContext';
import { useMessage } from '@/contexts/MessageContext';
import { useProductsMap } from '@/hooks/useProductsMap';
import { logger } from '@/utils/logger';
import { KPICard } from '@/components/KPICard';

function toYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function defaultFromYmd(): string {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return toYmd(d);
}

const TENANT_APP_WEB = (process.env.REACT_APP_TENANT_APP_URL || '').trim();

type PlatformStats = {
  total?: number;
  successful?: number;
  failed?: number;
  successRate?: number;
  byAction?: { _id: string; count: number }[];
  byResourceType?: { _id: string; count: number }[];
};

type BillingStats = {
  total?: number;
  byEvent?: { _id: string; count: number }[];
  byOutcome?: { _id: string; count: number }[];
};

type UpcomingNotificationRow = {
  scheduledDate: string;
  event: string;
  eventLabel?: string;
  tenantId: string;
  tenantName?: string;
  invoiceId?: string;
  paymentId?: string;
  dueDate?: string;
  competenceDueYmd?: string;
  billingPeriod?: string;
  daysPastCompetenceDue?: number;
  nextAutomatedReminderYmd?: string | null;
  nextAutomatedReminderEvent?: string | null;
  automatedReminderSlots?: Array<{ scheduledYmd: string; event: string }>;
  amountCents?: number;
  channels: string[];
  recipientUsers: Array<{ name?: string; email?: string }>;
  manualDispatchAvailable?: boolean;
  manualAppDispatchAvailable?: boolean;
  previewSource?: string;
  accessReason?: string;
  subscriptionActive?: boolean;
};

/** Competência para texto curto (MM/YYYY). */
function competenceMmYyyyFromRow(row: UpcomingNotificationRow): string {
  const bp = row.billingPeriod?.trim();
  if (bp && /^\d{4}-(0[1-9]|1[0-2])$/.test(bp)) {
    const [y, m] = bp.split('-');
    return `${m}/${y}`;
  }
  const ymd =
    row.competenceDueYmd ||
    (typeof row.dueDate === 'string' && row.dueDate.length >= 10
      ? row.dueDate.slice(0, 10)
      : undefined) ||
    row.scheduledDate;
  if (ymd && ymd.length >= 10) {
    const y = ymd.slice(0, 4);
    const mo = ymd.slice(5, 7);
    if (/^\d{4}$/.test(y) && /^\d{2}$/.test(mo)) return `${mo}/${y}`;
  }
  return '—';
}

type TabKey = 'platform' | 'billing';

export const AuditLogsPage: React.FC = () => {
  const { showError, showSuccess } = useMessage();
  const { selectedClients, selectedProductIds } = useFilters();
  const { setRefreshCallback } = useRefresh();
  const productsById = useProductsMap();

  const [tab, setTab] = useState<TabKey>('platform');

  const [startDate, setStartDate] = useState(defaultFromYmd);
  const [endDate, setEndDate] = useState(toYmd(new Date()));

  const [platformLogs, setPlatformLogs] = useState<any[]>([]);
  const [platformPage, setPlatformPage] = useState(0);
  const [platformRowsPerPage, setPlatformRowsPerPage] = useState(25);
  const [platformTotal, setPlatformTotal] = useState(0);
  const [platformAction, setPlatformAction] = useState('');
  const [platformSuccess, setPlatformSuccess] = useState<string>('');
  const [platformStats, setPlatformStats] = useState<PlatformStats | null>(null);

  const [billingItems, setBillingItems] = useState<any[]>([]);
  const [billingPage, setBillingPage] = useState(0);
  const [billingRowsPerPage, setBillingRowsPerPage] = useState(25);
  const [billingTotal, setBillingTotal] = useState(0);
  const [billingEvent, setBillingEvent] = useState('');
  const [billingOutcome, setBillingOutcome] = useState('');
  const [billingPaymentId, setBillingPaymentId] = useState('');
  const [billingStats, setBillingStats] = useState<BillingStats | null>(null);

  const [upcomingOpen, setUpcomingOpen] = useState(false);
  const [upcomingLoading, setUpcomingLoading] = useState(false);
  const [upcomingRows, setUpcomingRows] = useState<UpcomingNotificationRow[]>([]);
  const [upcomingMeta, setUpcomingMeta] = useState<Record<string, unknown> | null>(null);
  /** tenantId explícito na modal (ou filtro global se vazio). */
  const [upcomingTenantIdInput, setUpcomingTenantIdInput] = useState('');
  const [dispatchingKey, setDispatchingKey] = useState<string | null>(null);
  const [dispatchConfirmRow, setDispatchConfirmRow] = useState<UpcomingNotificationRow | null>(
    null
  );

  const [loading, setLoading] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailTitle, setDetailTitle] = useState('');
  const [detailJson, setDetailJson] = useState('');

  const tenantFromGlobalFilter = useMemo(
    () => (selectedClients.length === 1 ? selectedClients[0] : undefined),
    [selectedClients]
  );
  const productFilterParams = useMemo((): Record<string, string> => {
    if (selectedProductIds.length === 1) {
      return { productId: selectedProductIds[0]! };
    }
    if (selectedProductIds.length > 1) {
      return { productIds: selectedProductIds.join(',') };
    }
    return {};
  }, [selectedProductIds]);

  const openJson = (title: string, payload: unknown) => {
    setDetailTitle(title);
    try {
      setDetailJson(JSON.stringify(payload, null, 2));
    } catch {
      setDetailJson(String(payload));
    }
    setDetailOpen(true);
  };

  const loadPlatformStats = useCallback(async () => {
    try {
      const params: Record<string, string> = { startDate, endDate };
      if (tenantFromGlobalFilter) params.tenantId = tenantFromGlobalFilter;
      Object.assign(params, productFilterParams);
      const res = await apiService.getAuditLogStats(params);
      if (res.data.success) setPlatformStats(res.data.data as PlatformStats);
    } catch (e) {
      logger.error('audit stats', e);
    }
  }, [startDate, endDate, tenantFromGlobalFilter, productFilterParams]);

  const loadPlatformLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = {
        page: platformPage + 1,
        limit: platformRowsPerPage,
        startDate,
        endDate,
        sortBy: 'timestamp',
        sortOrder: 'desc'
      };
      if (tenantFromGlobalFilter) params.tenantId = tenantFromGlobalFilter;
      Object.assign(params, productFilterParams);
      if (platformAction.trim()) params.action = platformAction.trim();
      if (platformSuccess === 'true' || platformSuccess === 'false')
        params.success = platformSuccess;

      const response = await apiService.getAuditLogs(params);
      if (response.data.success) {
        setPlatformLogs(response.data.data);
        setPlatformTotal(response.data.pagination?.total ?? 0);
      }
    } catch (error) {
      logger.error('Error loading audit logs', error);
      showError('Erro ao carregar logs da plataforma.');
    } finally {
      setLoading(false);
    }
  }, [
    platformPage,
    platformRowsPerPage,
    startDate,
    endDate,
    tenantFromGlobalFilter,
    platformAction,
    platformSuccess,
    showError,
    productFilterParams
  ]);

  const loadBillingStats = useCallback(async () => {
    try {
      const params: Record<string, string> = { startDate, endDate };
      if (tenantFromGlobalFilter) params.tenantId = tenantFromGlobalFilter;
      Object.assign(params, productFilterParams);
      const res = await apiService.getBillingNotificationAuditStats(params);
      if (res.data.success) setBillingStats(res.data.data as BillingStats);
    } catch (e) {
      logger.error('billing audit stats', e);
    }
  }, [startDate, endDate, tenantFromGlobalFilter, productFilterParams]);

  const loadBillingLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = {
        page: billingPage + 1,
        limit: billingRowsPerPage,
        startDate,
        endDate
      };
      if (tenantFromGlobalFilter) params.tenantId = tenantFromGlobalFilter;
      Object.assign(params, productFilterParams);
      if (billingEvent.trim()) params.event = billingEvent.trim();
      if (billingOutcome.trim()) params.processingOutcome = billingOutcome.trim();
      if (billingPaymentId.trim()) params.paymentId = billingPaymentId.trim();

      const response = await apiService.getBillingNotificationAudits(params);
      if (response.data.success) {
        setBillingItems(response.data.data);
        setBillingTotal(response.data.pagination?.total ?? 0);
      }
    } catch (error) {
      logger.error('Error loading billing notification audit', error);
      showError('Erro ao carregar auditoria de notificações de cobrança.');
    } finally {
      setLoading(false);
    }
  }, [
    billingPage,
    billingRowsPerPage,
    startDate,
    endDate,
    tenantFromGlobalFilter,
    billingEvent,
    billingOutcome,
    billingPaymentId,
    showError,
    productFilterParams
  ]);

  const refreshCurrent = useCallback(async () => {
    if (tab === 'platform') {
      await loadPlatformStats();
      await loadPlatformLogs();
    } else {
      await loadBillingStats();
      await loadBillingLogs();
    }
  }, [tab, loadPlatformStats, loadPlatformLogs, loadBillingStats, loadBillingLogs]);

  useEffect(() => {
    setPlatformPage(0);
    setBillingPage(0);
  }, [startDate, endDate, tenantFromGlobalFilter, selectedProductIds]);

  useEffect(() => {
    void refreshCurrent();
  }, [
    refreshCurrent,
    tab,
    platformPage,
    platformRowsPerPage,
    billingPage,
    billingRowsPerPage,
    selectedProductIds
  ]);

  useEffect(() => {
    setRefreshCallback(() => void refreshCurrent());
    return () => setRefreshCallback(null);
  }, [refreshCurrent, setRefreshCallback]);

  const handleExportPlatform = async () => {
    try {
      const params: Record<string, string> = { startDate, endDate };
      if (tenantFromGlobalFilter) params.tenantId = tenantFromGlobalFilter;
      Object.assign(params, productFilterParams);
      if (platformAction.trim()) params.action = platformAction.trim();
      if (platformSuccess === 'true' || platformSuccess === 'false')
        params.success = platformSuccess;

      const response = await apiService.exportAuditLogs(params);
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-logs-${Date.now()}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      showSuccess('Exportação concluída.');
    } catch (error) {
      logger.error('Error exporting audit logs', error);
      showError('Erro ao exportar CSV.');
    }
  };

  const handleExportBilling = async () => {
    try {
      const params: Record<string, string> = { startDate, endDate };
      if (tenantFromGlobalFilter) params.tenantId = tenantFromGlobalFilter;
      Object.assign(params, productFilterParams);
      if (billingEvent.trim()) params.event = billingEvent.trim();
      if (billingOutcome.trim()) params.processingOutcome = billingOutcome.trim();
      if (billingPaymentId.trim()) params.paymentId = billingPaymentId.trim();

      const response = await apiService.exportBillingNotificationAudits(params);
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `billing-notification-audit-${Date.now()}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      showSuccess('Exportação concluída.');
    } catch (error) {
      logger.error('Error exporting billing audit', error);
      showError('Erro ao exportar CSV.');
    }
  };

  const channelsLabel = (row: any) => {
    const ch = Array.isArray(row.channels) ? row.channels : [];
    return ch.map((c: any) => `${c.channel}:${c.status}`).join(' · ') || '—';
  };

  const formatBrlFromCents = (cents?: number) => {
    if (cents == null || !Number.isFinite(cents)) return '—';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
      cents / 100
    );
  };

  const loadUpcomingNotifications = useCallback(
    async (forcedTenantId?: string) => {
      setUpcomingLoading(true);
      try {
        const params: Record<string, string | number> = { daysAhead: 30 };
        const resolved =
          forcedTenantId !== undefined
            ? forcedTenantId.trim() || (tenantFromGlobalFilter || '').trim()
            : (upcomingTenantIdInput || '').trim() || (tenantFromGlobalFilter || '').trim();
        if (resolved) params.tenantId = resolved;
        const res = await apiService.getBillingNotificationUpcoming(params);
        if (res.data.success) {
          setUpcomingRows((res.data.data as UpcomingNotificationRow[]) || []);
          setUpcomingMeta((res.data as { meta?: Record<string, unknown> }).meta ?? null);
        } else {
          setUpcomingRows([]);
          setUpcomingMeta(null);
          showError(
            String((res.data as { message?: string }).message || 'Falha ao carregar prévia.')
          );
        }
      } catch (error) {
        logger.error('upcoming billing notifications', error);
        setUpcomingRows([]);
        setUpcomingMeta(null);
        showError('Erro ao carregar próximas notificações.');
      } finally {
        setUpcomingLoading(false);
      }
    },
    [tenantFromGlobalFilter, upcomingTenantIdInput, showError]
  );

  const handleOpenUpcoming = () => {
    const snap = tenantFromGlobalFilter ?? '';
    setUpcomingTenantIdInput(snap);
    setUpcomingOpen(true);
    void loadUpcomingNotifications(snap);
  };

  const formatBillingSlotEvent = (ev: string) => {
    if (ev === 'invoice.due_reminder') return 'Antes do vencimento';
    if (ev === 'payment.overdue_reminder') return 'Após vencimento';
    return ev;
  };

  const openDispatchConfirm = (row: UpcomingNotificationRow) => {
    const canApp = row.manualAppDispatchAvailable === true;
    const canBilling = row.manualDispatchAvailable === true;
    if (!canApp && !canBilling) {
      showError(
        'Sem caminho de disparo disponível: o cliente não tem fatura/IDs no vover-billing e o produto não sinalizou disparo manual. Confirme urls.appApi, urls.appApiSecret no registry e rotas internal no produto.'
      );
      return;
    }
    setDispatchConfirmRow(row);
  };

  const executeDispatchRow = useCallback(
    async (row: UpcomingNotificationRow, channels: string[]) => {
      const canApp = row.manualAppDispatchAvailable === true;
      const key = `${canApp ? 'app' : 'billing'}|${row.tenantId}|${channels.join('+')}`;
      setDispatchingKey(key);
      try {
        if (canApp) {
          const res = await apiService.dispatchBillingNotification({
            mode: 'app_competence',
            tenantId: row.tenantId,
            event: 'app.competence_reminder',
            channels
          });
          const data = res.data as { success?: boolean; message?: string };
          if (res.status < 400 && data?.success !== false) {
            const sent =
              channels.includes('email') && channels.includes('push')
                ? 'E-mail e push enviados.'
                : channels.includes('email')
                  ? 'E-mail enviado.'
                  : 'Push enviado.';
            showSuccess(String(data?.message || sent));
          } else {
            showError(String(data?.message || `Resposta ${res.status} do servidor.`));
          }
          return;
        }
        const payload: { event: string; invoiceId?: string; paymentId?: string } = {
          event: row.event
        };
        if (row.invoiceId) payload.invoiceId = row.invoiceId;
        if (row.paymentId) payload.paymentId = row.paymentId;
        if (!payload.invoiceId && !payload.paymentId) {
          showError('Sem invoiceId ou paymentId para enviar ao billing.');
          return;
        }
        const res = await apiService.dispatchBillingNotification(payload);
        const data = res.data as { success?: boolean; message?: string; detail?: string };
        if (res.status < 400 && data?.success !== false) {
          showSuccess('Pedido de disparo enviado ao vover-billing.');
        } else {
          showError(data?.message || data?.detail || `Resposta ${res.status} do servidor.`);
        }
      } catch (e: unknown) {
        const ax = e as { response?: { data?: { message?: string; detail?: string } } };
        showError(
          ax.response?.data?.message ||
            ax.response?.data?.detail ||
            'Erro ao disparar (rede ou serviço indisponível).'
        );
      } finally {
        setDispatchingKey(null);
      }
    },
    [showError, showSuccess]
  );

  const handleDispatchSend = (channels: string[]) => {
    const row = dispatchConfirmRow;
    if (!row) return;
    setDispatchConfirmRow(null);
    void executeDispatchRow(row, channels);
  };

  return (
    <Box>
      <Card variant="outlined" sx={{ mb: 2, bgcolor: 'action.hover' }}>
        <CardContent>
          <Stack spacing={1.5}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1
              }}
            >
              <Typography variant="h4">Auditoria da plataforma</Typography>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={() => void refreshCurrent()}
                disabled={loading}
              >
                Atualizar
              </Button>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Centraliza atividades administrativas e notificações de cobrança no escopo de produtos
              selecionado. Use para responder "quem fez o quê", "qual cliente foi afetado" e "qual
              integração processou o evento".
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip
                size="small"
                color="primary"
                label={`${selectedProductIds.length || 'Todos'} produto(s)`}
              />
              <Chip size="small" variant="outlined" label="control.audit_logs" />
              <Chip size="small" variant="outlined" label="billing_notification_audit" />
              <Chip size="small" variant="outlined" label="Retenção típica: ~90 dias" />
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="De"
              type="date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Até"
              type="date"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">
              {tenantFromGlobalFilter ? (
                <>
                  Filtro de cliente (topo): <strong>{tenantFromGlobalFilter}</strong>
                </>
              ) : (
                'Selecione um único cliente no filtro global para restringir por tenant; caso contrário vê-se toda a plataforma.'
              )}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab value="platform" label="Atividades da plataforma" />
        <Tab value="billing" label="Notificações de cobrança" />
      </Tabs>

      {tab === 'platform' && (
        <>
          {platformStats && (
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6} md={3}>
                <KPICard
                  title="Eventos (período)"
                  value={platformStats.total ?? 0}
                  color="primary"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <KPICard title="Sucesso" value={platformStats.successful ?? 0} color="success" />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <KPICard title="Falha" value={platformStats.failed ?? 0} color="error" />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <KPICard
                  title="Taxa de sucesso"
                  value={`${(platformStats.successRate ?? 0).toFixed(1)}%`}
                  color="info"
                />
              </Grid>
              {(platformStats.byAction?.length ?? 0) > 0 && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Top ações
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {platformStats.byAction!.slice(0, 12).map(row => (
                      <Chip
                        key={row._id}
                        size="small"
                        label={`${row._id} (${row.count})`}
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Grid>
              )}
            </Grid>
          )}

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2, alignItems: 'center' }}>
            <TextField
              size="small"
              label="Ação (contém)"
              value={platformAction}
              onChange={e => setPlatformAction(e.target.value)}
              sx={{ minWidth: 200 }}
            />
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Sucesso</InputLabel>
              <Select
                value={platformSuccess}
                label="Sucesso"
                onChange={e => setPlatformSuccess(String(e.target.value))}
              >
                <MenuItem value="">(todos)</MenuItem>
                <MenuItem value="true">Sim</MenuItem>
                <MenuItem value="false">Não</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="contained"
              startIcon={<Download />}
              onClick={() => void handleExportPlatform()}
            >
              Exportar CSV
            </Button>
          </Box>

          <TableContainer component={Paper} sx={{ mt: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Data/Hora</TableCell>
                  <TableCell>Produto</TableCell>
                  <TableCell>Ação</TableCell>
                  <TableCell>Utilizador</TableCell>
                  <TableCell>Cliente</TableCell>
                  <TableCell>Recurso</TableCell>
                  <TableCell>Estado</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      A carregar…
                    </TableCell>
                  </TableRow>
                ) : platformLogs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      Nenhum registo
                    </TableCell>
                  </TableRow>
                ) : (
                  platformLogs.map(log => (
                    <TableRow
                      key={log._id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => openJson(`audit_logs / ${log.action}`, log)}
                    >
                      <TableCell>
                        {new Date(log.timestamp || log.createdAt).toLocaleString('pt-BR')}
                      </TableCell>
                      <TableCell>
                        {log.productId ? (
                          <ProductCell
                            productId={log.productId}
                            name={productsById.get(log.productId)?.displayName}
                            avatarUrl={productsById.get(log.productId)?.avatarUrl}
                          />
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 260 }}>{log.action}</TableCell>
                      <TableCell>{log.userEmail || log.userId || '—'}</TableCell>
                      <TableCell>
                        {log.tenantId ? (
                          <ClientCell
                            name={log.companyName || log.tenantName}
                            tenantId={log.tenantId}
                            size="small"
                          />
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 200 }}>
                        {log.resourceType
                          ? `${log.resourceType}${log.resourceId ? ` (${log.resourceId})` : ''}`
                          : '—'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={log.success ? 'OK' : 'Falha'}
                          color={log.success ? 'success' : 'error'}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={platformTotal}
            page={platformPage}
            onPageChange={(_, p) => setPlatformPage(p)}
            rowsPerPage={platformRowsPerPage}
            onRowsPerPageChange={e => {
              setPlatformRowsPerPage(parseInt(e.target.value, 10));
              setPlatformPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Linhas"
          />
        </>
      )}

      {tab === 'billing' && (
        <>
          {billingStats && (
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={4}>
                <KPICard
                  title="Registos (período)"
                  value={billingStats.total ?? 0}
                  color="primary"
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <Typography variant="subtitle2" gutterBottom>
                  Por evento
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(billingStats.byEvent || []).map(row => (
                    <Chip
                      key={row._id || 'empty'}
                      size="small"
                      label={`${row._id || '—'} (${row.count})`}
                    />
                  ))}
                </Box>
                <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
                  Por resultado
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(billingStats.byOutcome || []).map(row => (
                    <Chip
                      key={row._id || 'o'}
                      size="small"
                      variant="outlined"
                      label={`${row._id || '—'} (${row.count})`}
                    />
                  ))}
                </Box>
              </Grid>
            </Grid>
          )}

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2, alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Evento</InputLabel>
              <Select
                value={billingEvent}
                label="Evento"
                onChange={e => setBillingEvent(String(e.target.value))}
              >
                <MenuItem value="">(todos)</MenuItem>
                <MenuItem value="invoice.due_reminder">invoice.due_reminder</MenuItem>
                <MenuItem value="payment.overdue_reminder">payment.overdue_reminder</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Resultado</InputLabel>
              <Select
                value={billingOutcome}
                label="Resultado"
                onChange={e => setBillingOutcome(String(e.target.value))}
              >
                <MenuItem value="">(todos)</MenuItem>
                <MenuItem value="ok">ok</MenuItem>
                <MenuItem value="aborted_tenant_not_found">aborted_tenant_not_found</MenuItem>
                <MenuItem value="aborted_no_recipients">aborted_no_recipients</MenuItem>
                <MenuItem value="duplicate_webhook_ignored">duplicate_webhook_ignored</MenuItem>
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="paymentId"
              value={billingPaymentId}
              onChange={e => setBillingPaymentId(e.target.value)}
              sx={{ minWidth: 200 }}
            />
            <Button variant="outlined" startIcon={<ScheduleSend />} onClick={handleOpenUpcoming}>
              Próximas notificações
            </Button>
            <Button
              variant="contained"
              startIcon={<Download />}
              onClick={() => void handleExportBilling()}
            >
              Exportar CSV
            </Button>
          </Box>

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Quando</TableCell>
                  <TableCell>Produto</TableCell>
                  <TableCell>Evento</TableCell>
                  <TableCell>Tenant</TableCell>
                  <TableCell>Resultado</TableCell>
                  <TableCell>Canais</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      A carregar…
                    </TableCell>
                  </TableRow>
                ) : billingItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      Nenhum registo (confirme se o vover-app já grava em
                      billing_notification_audit)
                    </TableCell>
                  </TableRow>
                ) : (
                  billingItems.map(row => (
                    <TableRow
                      key={row._id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => openJson(`billing_notification_audit / ${row.event}`, row)}
                    >
                      <TableCell>
                        {row.createdAt ? new Date(row.createdAt).toLocaleString('pt-BR') : '—'}
                      </TableCell>
                      <TableCell>
                        {row.productId ? (
                          <ProductCell
                            productId={row.productId}
                            name={productsById.get(row.productId)?.displayName}
                            avatarUrl={productsById.get(row.productId)?.avatarUrl}
                          />
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell>{row.event}</TableCell>
                      <TableCell>
                        {row.tenantId ? (
                          <ClientCell
                            name={row.companyName || row.tenantName}
                            tenantId={row.tenantId}
                            size="small"
                          />
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={String(row.processingOutcome || '—')}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 360 }} title={channelsLabel(row)}>
                        {channelsLabel(row)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={billingTotal}
            page={billingPage}
            onPageChange={(_, p) => setBillingPage(p)}
            rowsPerPage={billingRowsPerPage}
            onRowsPerPageChange={e => {
              setBillingRowsPerPage(parseInt(e.target.value, 10));
              setBillingPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Linhas"
          />
        </>
      )}

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{detailTitle}</DialogTitle>
        <DialogContent>
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 1,
              bgcolor: 'grey.100',
              borderRadius: 1,
              overflow: 'auto',
              fontSize: 12,
              maxHeight: '70vh'
            }}
          >
            {detailJson}
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog open={upcomingOpen} onClose={() => setUpcomingOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Próximas notificações de cobrança (prévia)</DialogTitle>
        <DialogContent>
          {upcomingMeta && typeof upcomingMeta.previewNote === 'string' && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {String(upcomingMeta.previewNote)}
            </Alert>
          )}
          {upcomingMeta && upcomingMeta.configured === false && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {String(upcomingMeta.message || 'Billing não configurado.')}
            </Alert>
          )}
          <TextField
            label="Cliente (tenantId)"
            placeholder="Cole o tenantId e clique em Atualizar prévia"
            value={upcomingTenantIdInput}
            onChange={e => setUpcomingTenantIdInput(e.target.value)}
            fullWidth
            size="small"
            sx={{ mb: 2 }}
            helperText={
              tenantFromGlobalFilter
                ? `Se vazio, usa o filtro global: ${tenantFromGlobalFilter}`
                : 'Opcional: use após scripts no Mongo ou para um cliente que não aparece na lista geral do vover-app.'
            }
          />
          {upcomingLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={36} />
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Venc. competência</TableCell>
                    <TableCell>Próx. automático (billing)</TableCell>
                    <TableCell>Situação</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell>Valor / venc. fatura</TableCell>
                    <TableCell>IDs</TableCell>
                    <TableCell>Utilizadores (tenant)</TableCell>
                    <TableCell>Canais</TableCell>
                    <TableCell align="right">Ação</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {upcomingRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        Nenhum alerta de cobrança neste momento (ou filtro sem resultados).
                      </TableCell>
                    </TableRow>
                  ) : (
                    upcomingRows.map(row => {
                      const canApp = row.manualAppDispatchAvailable === true;
                      const canBilling = row.manualDispatchAvailable === true;
                      const users = row.recipientUsers || [];
                      const userPreview =
                        users.length === 0
                          ? '—'
                          : users
                              .slice(0, 4)
                              .map(u => u.email || u.name || '—')
                              .join(', ') + (users.length > 4 ? ` +${users.length - 4}` : '');
                      const competenceYmd =
                        row.competenceDueYmd ||
                        (typeof row.dueDate === 'string' && row.dueDate.length >= 10
                          ? row.dueDate.slice(0, 10)
                          : undefined) ||
                        row.scheduledDate;
                      const daysPast =
                        row.daysPastCompetenceDue != null &&
                        Number.isFinite(row.daysPastCompetenceDue)
                          ? row.daysPastCompetenceDue
                          : null;
                      const extraSlots = (row.automatedReminderSlots || []).filter(
                        s => s.scheduledYmd !== row.nextAutomatedReminderYmd
                      );
                      return (
                        <TableRow key={`${row.tenantId}|${row.scheduledDate}|${row.event}`}>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            <Typography variant="body2">{competenceYmd}</Typography>
                            {daysPast != null && daysPast > 0 ? (
                              <Typography variant="caption" color="text.secondary" display="block">
                                venceu há {daysPast} dia(s)
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell sx={{ maxWidth: 200 }}>
                            {row.nextAutomatedReminderYmd ? (
                              <>
                                <Typography variant="body2">
                                  {row.nextAutomatedReminderYmd}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                >
                                  {formatBillingSlotEvent(row.nextAutomatedReminderEvent || '')}
                                </Typography>
                                {extraSlots.length > 0 ? (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                    sx={{ mt: 0.5 }}
                                  >
                                    +{extraSlots.length} data(s) na janela (prévia)
                                  </Typography>
                                ) : null}
                              </>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                —
                              </Typography>
                            )}
                            {row.accessReason === 'no_current_invoice' ? (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                                sx={{ mt: 0.5 }}
                              >
                                Sem fatura no billing: o cron não envia webhooks até existir fatura;
                                as datas são o calendário que valeria com o mesmo vencimento.
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell sx={{ maxWidth: 280 }} title={row.eventLabel || row.event}>
                            <Typography variant="body2">{row.eventLabel || row.event}</Typography>
                            {row.accessReason ? (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {row.accessReason}
                                {row.subscriptionActive === false ? ' · sem assinatura ativa' : ''}
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell>
                            <ClientCell
                              {...(row.tenantName != null ? { name: row.tenantName } : {})}
                              tenantId={row.tenantId}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {row.invoiceId || row.paymentId
                                ? formatBrlFromCents(row.amountCents)
                                : '—'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {row.invoiceId || row.paymentId
                                ? `venc. fatura ${row.dueDate || '—'}`
                                : 'sem fatura no billing'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ maxWidth: 200, fontSize: 11 }}>
                            {row.invoiceId ? (
                              <>
                                inv. <CopyableId value={row.invoiceId} label="invoiceId" />
                              </>
                            ) : (
                              <span>—</span>
                            )}
                            {row.paymentId ? (
                              <>
                                <br />
                                pay. <CopyableId value={row.paymentId} label="paymentId" />
                              </>
                            ) : null}
                          </TableCell>
                          <TableCell sx={{ maxWidth: 260 }} title={userPreview}>
                            <Typography variant="body2" noWrap>
                              {userPreview}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {(row.channels || []).map(c => (
                              <Chip key={c} size="small" label={c} sx={{ mr: 0.5, mb: 0.5 }} />
                            ))}
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={!!dispatchingKey || (!canApp && !canBilling)}
                              onClick={() => openDispatchConfirm(row)}
                            >
                              {dispatchingKey
                                ? '…'
                                : !canApp && !canBilling
                                  ? 'Indisponível'
                                  : 'Enviar MSG'}
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => void loadUpcomingNotifications()} disabled={upcomingLoading}>
            Atualizar prévia
          </Button>
          <Button onClick={() => setUpcomingOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={dispatchConfirmRow != null}
        onClose={() => {
          if (!dispatchingKey) setDispatchConfirmRow(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Enviar lembrete manual</DialogTitle>
        <DialogContent dividers>
          {dispatchConfirmRow
            ? (() => {
                const r = dispatchConfirmRow;
                const empresa = (r.tenantName || '').trim() || r.tenantId;
                const comp = competenceMmYyyyFromRow(r);
                const venc = r.dueDate
                  ? r.dueDate.slice(0, 10).split('-').reverse().join('/')
                  : '—';
                const valor =
                  r.amountCents != null ? formatBrlFromCents(r.amountCents) : 'A calcular';
                const recipients =
                  (r.recipientUsers || [])
                    .map(u => u.email || u.name || '')
                    .filter(Boolean)
                    .join(', ') || '—';
                const appUrl = TENANT_APP_WEB || 'vcondo.vover.com.br';
                return (
                  <Stack spacing={2}>
                    {/* Dados da cobrança */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                      <Typography variant="body2">
                        <strong>Cliente:</strong> {empresa}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Competência:</strong> {comp}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Vencimento:</strong> {venc}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Valor:</strong> {valor}
                      </Typography>
                    </Box>

                    {/* Preview E-mail */}
                    <Box
                      sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}
                    >
                      <Typography
                        variant="caption"
                        color="primary"
                        fontWeight="bold"
                        display="block"
                        mb={1}
                      >
                        📧 E-MAIL
                      </Typography>
                      <Typography variant="caption" display="block" color="text.secondary">
                        <strong>Assunto:</strong> Lembrete: pendência de pagamento — {comp}
                      </Typography>
                      <Typography variant="caption" display="block" color="text.secondary">
                        <strong>Para:</strong> {recipients}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ mt: 1, fontSize: '0.78rem', lineHeight: 1.5 }}
                      >
                        Olá,
                        <br />
                        Lembrete sobre a assinatura de <strong>{empresa}</strong> (competência{' '}
                        <strong>{comp}</strong>).
                        <br />
                        Há <strong>pendência de pagamento</strong>. Acesse <strong>{appUrl}</strong>{' '}
                        e vá para <strong>Financeiro</strong> para gerar boleto ou PIX.
                      </Typography>
                    </Box>

                    {/* Preview Push */}
                    <Box
                      sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}
                    >
                      <Typography
                        variant="caption"
                        color="primary"
                        fontWeight="bold"
                        display="block"
                        mb={1}
                      >
                        🔔 PUSH
                      </Typography>
                      <Typography variant="caption" display="block" color="text.secondary">
                        <strong>Título:</strong> Aviso de cobrança
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5, fontSize: '0.78rem' }}>
                        {empresa} · Competência {comp}. Acesse vcondo.vover.com.br e vá para
                        Financeiro para efetuar o pagamento.
                      </Typography>
                    </Box>
                  </Stack>
                );
              })()
            : null}
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1, p: 2 }}>
          <Button onClick={() => setDispatchConfirmRow(null)} disabled={!!dispatchingKey}>
            Cancelar
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            variant="outlined"
            disabled={!!dispatchingKey}
            onClick={() => handleDispatchSend(['email'])}
          >
            {dispatchingKey ? '…' : 'Enviar E-mail'}
          </Button>
          <Button
            variant="outlined"
            disabled={!!dispatchingKey}
            onClick={() => handleDispatchSend(['push'])}
          >
            {dispatchingKey ? '…' : 'Enviar Push'}
          </Button>
          <Button
            variant="contained"
            disabled={!!dispatchingKey}
            onClick={() => handleDispatchSend(['email', 'push'])}
          >
            {dispatchingKey ? '…' : 'Enviar Ambos'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AuditLogsPage;
